/**
 * 工具函数集合 - 迁移自 Util.js 和 me.global.js
 */

/**
 * 日期格式化
 * @param {Date|string|number} timespan - 时间
 * @param {string} format - 格式字符串，如 'yyyy-MM-dd hh:mm:ss'
 */
export function dateToString(timespan, format = 'yyyy-MM-dd hh:mm:ss') {
  if (!timespan) return '-'

  const temp = /^\/Date\((\d+)\)\/$/.exec(String(timespan))
  if (temp) {
    timespan = parseFloat(temp[1])
  }

  const date = new Date(timespan)
  if (isNaN(date.getTime())) return '-'

  const o = {
    'M+': date.getMonth() + 1,
    'd+': date.getDate(),
    'h+': date.getHours(),
    'm+': date.getMinutes(),
    's+': date.getSeconds(),
    'q+': Math.floor((date.getMonth() + 3) / 3),
    S: date.getMilliseconds(),
  }

  if (/(y+)/.test(format)) {
    format = format.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length))
  }

  for (const k in o) {
    if (new RegExp('(' + k + ')').test(format)) {
      format = format.replace(
        RegExp.$1,
        RegExp.$1.length === 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length),
      )
    }
  }

  return format
}

/**
 * GMT时间字符串转格式化字符串
 */
export function timeGMTToString(str, format = 'yyyy-MM-dd') {
  if (!str) return '--'
  return dateToString(new Date(str), format)
}

/**
 * 金额格式化
 * @param {number} num - 数字
 * @param {number} precision - 小数位
 * @param {string} separator - 分隔符
 */
export function formatMoney(num, precision = 2, separator = ',') {
  num = num || 0
  if (!isNaN(parseFloat(num)) && isFinite(num)) {
    num = Number(num)
    num = (precision !== undefined ? num.toFixed(precision) : num).toString()
    const parts = num.split('.')
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + separator)
    return parts.join('.')
  }
  return 'NaN'
}

/**
 * 数字转中文大写
 */
export function numberToChinese(n) {
  if (!/^(0|[1-9]\d*)(\.\d+)?$/.test(n)) return ''
  const unit = '千百拾亿千百拾万千百拾元角分'
  let str = ''
  n += '00'
  const p = n.indexOf('.')
  if (p >= 0) n = n.substring(0, p) + n.substr(p + 1, 2)
  const u = unit.substr(unit.length - n.length)
  for (let i = 0; i < n.length; i++) {
    str += '零壹贰叁肆伍陆柒捌玖'.charAt(n.charAt(i)) + u.charAt(i)
  }
  return str
    .replace(/零(千|百|拾|角)/g, '零')
    .replace(/(零)+/g, '零')
    .replace(/零(万|亿|元)/g, '$1')
    .replace(/(亿)万|壹(拾)/g, '$1$2')
    .replace(/^元零?|零分/g, '')
    .replace(/元$/g, '')
}

/**
 * 文件大小格式化
 */
export function formatSize(size) {
  if (!size) return '0M'
  const units = ['B', 'KB', 'M', 'G', 'T']
  let index = 0
  while (size > 1024) {
    size = Math.round(((size * 1.0) / 1024) * 100) / 100
    if (index >= units.length - 1) break
    index++
  }
  return Math.round(size * 10) / 10 + units[index]
}

/**
 * 保留两位小数
 */
export function fixedNumber(number) {
  if (!number) return '0.00'
  return parseFloat(number).toFixed(2)
}

/**
 * 去除 HTML 标签
 */
export function htmlFormat(msg) {
  if (!msg) return ''
  return msg.replace(/<\/?.+?>/g, '').replace(/&nbsp;/g, '')
}

/**
 * 手机号验证
 */
export function isPhone(phone) {
  return /^1[3456789]\d{9}$/.test(phone) || /^((0\d{2,3})-)(\d{7,8})(-(\d{3,}))?$/.test(phone)
}

/**
 * 数字验证
 */
export function isNum(num) {
  return /^\d+(\.\d+)?$/.test(num)
}

/**
 * 整数验证
 */
export function isInteger(num) {
  return /^\d+$/.test(num)
}

/**
 * 身份证验证
 */
export function isIdNumber(str) {
  return /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/.test(str)
}

/**
 * 邮箱验证
 */
export function isMail(str) {
  return /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/.test(str)
}

/**
 * 数字前补零
 */
export function prefixInteger(num, length) {
  return (Array(length).join('0') + num).slice(-length)
}

/**
 * 数字格式化（万）
 */
export function formatNumber(num) {
  if (num < 10000) return num
  return Math.round(((num * 1.0) / 10000) * 100) / 100 + '万'
}

/**
 * 数组转 Map
 * @param {Array} arr - 数组
 * @param {string} idField - key 字段名
 * @param {string} idField2 - 扩展 key 字段名
 * @param {string} char - 连接字符
 */
export function toMap(arr, idField, idField2, char) {
  if (!arr) return {}
  const map = {}
  arr.forEach((item) => {
    let field = item[idField]
    if (!field) return
    if (idField2) {
      field += char + item[idField2]
    }
    map[field] = item
  })
  return map
}

/**
 * 数组按字段分组
 */
export function toGroup(arr, field) {
  const group = {}
  if (!arr || arr.length === 0) return group
  arr.forEach((a) => {
    if (!group[a[field]]) {
      group[a[field]] = []
    }
    group[a[field]].push(a)
  })
  return group
}

/**
 * ============================================
 * API 数据缓存工具
 * ============================================
 * 缓存策略：
 * - 首次调用时从接口获取数据并缓存到 localStorage
 * - 同一会话内后续调用直接使用缓存
 * - 刷新浏览器后重新请求（内存标记重置）
 * - forceRefresh: true 时强制刷新缓存
 */

// 缓存状态存储（内存中）
const cacheStatus = {}

/**
 * 创建带缓存的 API 请求函数
 * @param {Function} requestFn - 原始请求函数
 * @param {string} cacheKeyPrefix - 缓存 key 前缀
 * @param {Function} getCacheKeySuffix - 根据请求参数生成缓存 key 后缀
 * @returns {Object} { cachedFn, clearCache }
 */
export function createCachedRequest(requestFn, cacheKeyPrefix, getCacheKeySuffix) {
  // 标记是否已从接口加载过（内存中）
  const loadedKey = `${cacheKeyPrefix}_loaded`

  /**
   * 带缓存的请求函数
   * @param {Object} data - 请求参数
   * @param {Boolean} forceRefresh - 是否强制刷新
   */
  async function cachedFn(data = {}, forceRefresh = false) {
    const suffix = getCacheKeySuffix ? getCacheKeySuffix(data) : 'default'
    const cacheKey = `${cacheKeyPrefix}_${suffix}`

    // 如果不是强制刷新，且内存标记显示已加载，尝试使用缓存
    if (!forceRefresh && cacheStatus[loadedKey]) {
      const cached = localStorage.getItem(cacheKey)
      if (cached) {
        try {
          return JSON.parse(cached)
        } catch {
          // 解析失败，继续请求接口
        }
      }
    }

    // 从接口获取数据
    const result = await requestFn(data)

    // 缓存结果（添加错误处理，防止超出配额）
    if (result) {
      try {
        localStorage.setItem(cacheKey, JSON.stringify(result))
        cacheStatus[loadedKey] = true
      } catch (e) {
        // 配额超出或其他错误，静默处理，不影响功能
        if (e.name === 'QuotaExceededError') {
          console.warn(`localStorage 配额不足，无法缓存 ${cacheKey}`)
        } else {
          console.warn(`缓存失败 ${cacheKey}:`, e)
        }
      }
    }

    return result
  }

  /**
   * 清除缓存
   * @param {Object} data - 请求参数（用于确定缓存 key）
   */
  function clearCache(data = {}) {
    const suffix = getCacheKeySuffix ? getCacheKeySuffix(data) : 'default'
    const cacheKey = `${cacheKeyPrefix}_${suffix}`
    localStorage.removeItem(cacheKey)
  }

  return { cachedFn, clearCache }
}

/**
 * 清除所有带指定前缀的缓存
 * @param {string} prefix - 缓存 key 前缀
 */
export function clearCacheByPrefix(prefix) {
  const keysToRemove = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key.startsWith(prefix)) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key))
}
